import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Typography,
  Chip,
  Box,
  Fade,
  Zoom,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import CloseButton from './Common/CloseButton';
import PluginMemoryDialog from './PluginMemoryDialog';
import { useConnectorCreator } from './ConnectorCreatorProvider';

// Import all icons
import icon_genai from '../images/flow_genai.png'
import icon_createfile from '../images/flow_createfile.png';
import icon_copyfile from '../images/flow_copyfile.png';
import icon_createfolder from '../images/folder.png';
import icon_createdocx from '../images/docx.png';
import icon_createexcel from '../images/excelexport.png';
import icon_createpdf from '../images/flow_createpdf.png'; // Reusing doc icon for now
import icon_sendemail from '../images/flow_sendemail.png';
import icon_callmcp from '../images/flow_callmcp.png';
import icon_sendapi from '../images/flow_sendapi.png';
import icon_sendmsgteams from '../images/flow_teamssendmsg.png';
import icon_createtagbrowseweb from '../images/flow_createtagbrowseweb.png';
import icon_cmqverifydoctor from '../images/flow_verifydoctor.png'; // Reusing the same icon for now
import icon_oiiqverifynurse from '../images/flow_verifynurse.png'; // Reusing the same icon for now
import icon_validatelicense from '../images/validateform.png'; // Icon for rule-based license validation
import icon_cropimage from '../images/flow_createfile.png'; // Using createfile icon temporarily for crop image
import icon_javascript from '../images/flow_callmcp.png'; // Using callmcp icon temporarily for javascript
import icon_default from '../images/logotransparent.png'; // Default icon for plugin actions without specified icons
const AddActionMenu = ({
  open,
  onClose,
  onMenuItemSelect,
  pluginActions = [],
  caseid = null
}) => {
  const { t } = useTranslation();
  const { sendMessage } = useWebSocket();
  const { openConnectorCreator, closeConnectorCreator } = useConnectorCreator();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedAction, setSelectedAction] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [createdActionTypeToOpen, setCreatedActionTypeToOpen] = useState('');
  const [loadingIcons, setLoadingIcons] = useState(new Set());
  const [selectedScope, setSelectedScope] = useState('event'); // Default scope for new actions
  const [pluginMemoryDialogState, setPluginMemoryDialogState] = useState({
    open: false,
    pluginName: '',
    memory: null
  });

  // we do not allow to add generateWidget because it is add automatically when you create a new widget.
  const menuItems = useMemo(() => {
    const baseItems = [
    {
      type: 'createfile',
      icon: icon_createfile,
      alt: 'Create file',
      title: t('createfile', 'Create File'),
      description: t('workflow.create_file.description', 'Create new files with custom or AI-generated content'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createjsonfile',
      icon: icon_createfile,
      alt: 'Create JSON file',
      title: t('createjsonfile', 'Create JSON File'),
      description: t('workflow.create_json_file.description', 'Generate JSON documents using structured tag-driven templates'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createzipfile',
      icon: icon_createfile,
      alt: 'Create ZIP archive',
      title: t('createzipfile', 'Create ZIP Archive'),
      description: t('workflow.create_zip.description', 'Bundle the event file and other documents into a single ZIP package'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createxmlfile',
      icon: icon_createfile,
      alt: 'Create XML file',
      title: t('createxmlfile', 'Create XML File'),
      description: t('workflow.create_xml_file.description', 'Generate XML documents populated with selected tags and attributes'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'copyfile',
      icon: icon_copyfile,
      alt: 'Copy file',
      title: t('copy_file', 'Copy File'),
      description: t('workflow.copy_file.description', 'Copy existing files to new locations with dynamic paths'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createfolder',
      icon: icon_createfolder,
      alt: 'Create folder',
      title: t('createfolder', 'Create Folder'),
      description: t('workflow.create_folder.description', 'Create folders automatically with dynamic paths'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createproject',
      icon: icon_createfolder,
      alt: 'Create project',
      title: t('createproject', 'Create DataUnfold Project'),
      description: t('workflow.create_project.description', 'Create new projects automatically with templates and collections'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'cropimage',
      icon: icon_cropimage,
      alt: 'Crop Image',
      title: t('cropimage', 'Crop Image'),
      description: t('workflow.crop_image.description', 'Crop images and store in tag'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'processing'
    },
    {
      type: 'createdocx',
      icon: icon_createdocx,
      alt: 'Create Word Document',
      title: t('createdocx', 'Create Word Document'),
      description: t('workflow.create_docx.description', 'Create Word documents with custom or AI-generated content'),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low (if using AI generation)'),
      costLevel: 'low',
      category: 'export'
    },
    {
      type: 'createdocxtemplate',
      icon: icon_createdocx,
      alt: 'Create Word Document from Template',
      title: t('createdocxtemplate', 'Create Word Document from Template'),
      description: t('workflow.create_docx_template.description', 'Create Word documents from templates using Jinja2 with selected tags'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createdocxtemplatev2',
      icon: icon_createdocx,
      alt: 'Create Word Document from Template V2',
      title: t('createdocxtemplatev2', 'Create Word Document from Template V2'),
      description: t('workflow.create_docx_template_v2.description', 'Create Word documents by replacing [[tag]] placeholders directly to better preserve formatting'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'replacewordtext',
      icon: icon_createdocx,
      alt: 'Replace Word Text',
      title: t('replacewordtext', 'Replace Word Text'),
      description: t('workflow.replace_word_text.description', 'Open an existing Word document, find text across the whole document, and save a replaced copy'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'anonymizepii',
      icon: icon_createdocx,
      alt: 'Anonymize PII',
      title: t('anonymizepii', 'Anonymize PII'),
      description: t('workflow.anonymize_pii.description', 'Open an existing Word document, anonymize common PII patterns, and save a sanitized DOCX copy'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createexcelfromtemplate',
      icon: icon_createexcel,
      alt: 'Create Excel from Template',
      title: t('createexcelfromtemplate', 'Create Excel from Template'),
      description: t('workflow.create_excel_template.description', 'Create Excel workbooks from templates using {tag} placeholders'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'addpasswordexcel',
      icon: icon_createexcel,
      alt: 'Add Password to Excel',
      title: t('addpasswordexcel', 'Add Password to Excel'),
      description: t('workflow.add_password_excel.description', 'Add password protection to existing Excel workbooks'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'addpassworddocx',
      icon: icon_createdocx,
      alt: 'Add Password to DOCX',
      title: t('addpassworddocx', 'Add Password to DOCX'),
      description: t('workflow.add_password_docx.description', 'Add password protection to existing Word documents'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'convertdocxtopdf',
      icon: icon_createpdf,
      alt: 'Convert DOCX to PDF',
      title: t('convertdocxtopdf', 'Convert DOCX to PDF'),
      description: t('workflow.convert_docx_to_pdf.description', 'Convert Word documents to PDF format'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'export'
    },
    {
      type: 'createpdf',
      icon: icon_createpdf,
      alt: 'Create PDF',
      title: t('createpdf', 'Create PDF'),
      description: t('workflow.create_pdf.description', 'Create PDF documents with custom or AI-generated content'),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low (if using AI generation)'),
      costLevel: 'low',
      category: 'export'
    },
    {
      type: 'browseweb',
      icon: icon_createtagbrowseweb,
      alt: 'Browse Web',
      title: t('browseweb', 'Browse Web (Agent)'),
      description: t('workflow.browseweb.description', 'AI agent that can browse websites and extract data'),
      cost: t('workflow.ai_cost.high', 'AI Cost: High'),
      costLevel: 'high',
      displayText: t('workflow.browseweb', 'Generic Web (Web Agent)'),
      category: 'ai'
    },
    {
      type: 'computeruse',
      icon: icon_createtagbrowseweb,
      alt: 'Application Use',
      title: t('computeruse', 'Application Use (Desktop Agent)'),
      description: t('workflow.computeruse.description', 'AI agent that reads a Windows UI tree and uses an already-open desktop application'),
      cost: t('workflow.ai_cost.high', 'AI Cost: High'),
      costLevel: 'high',
      displayText: t('workflow.computeruse', 'Application Use (Desktop Agent)'),
      category: 'ai'
    },
    {
      type: 'cmqverifydoctor',
      icon: icon_cmqverifydoctor,
      alt: 'CMQ Verify Doctor (Web Agent)',
      title: t('cmqverifydoctor', 'CMQ Verify licence (Web Agent)'),
      description: t('workflow.cmqverifydoctor.description', 'Using browser, verify doctor registration and information from CMQ directory'),
      cost: t('workflow.ai_cost.high', 'AI Cost: High'),
      costLevel: 'high',
      displayText: t('workflow.cmqverifydoctor', 'CMQ Verify Doctor (Web Agent)'),
      category: 'ai'
    },
    {
      type: 'oiiqverifynurse',
      icon: icon_oiiqverifynurse,
      alt: 'OIIQ Verify Nurse (Web Agent)',
      title: t('oiiqverifynurse', 'OIIQ Verify licence (Web Agent)'),
      description: t('workflow.oiiqverifynurse.description', 'Using browser, verify nurse registration and information from OIIQ directory'),
      cost: t('workflow.ai_cost.high', 'AI Cost: High'),
      costLevel: 'high',
      displayText: t('workflow.oiiqverifynurse', 'OIIQ Verify Nurse (Web Agent)'),
      category: 'ai'
    },
    {
      type: 'generativeanswer',
      icon: icon_genai,
      alt: 'Generative Answer',
      title: t('generativeanswer', 'Generative Answer'),
      description: t('workflow.generativeanswer.description', 'Generate AI-powered responses based on your data and instructions'),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low'),
      costLevel: 'low',
      category: 'ai'
    },
    {
      type: 'generativeanswerfile',
      icon: icon_genai,
      alt: 'Generative Answer (File)',
      title: t('workflow.generativeanswerfile', 'File Generative Answer'),
      description: t('workflow.generativeanswerfile.description', 'Generate answers that include the current file content before responding'),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low'),
      costLevel: 'low',
      displayText: t('workflow.generativeanswerfile', 'File Generative Answer'),
      category: 'ai'
    },
    {
      type: 'simplejavascript',
      icon: icon_javascript,
      alt: 'Simple JavaScript',
      title: t('simplejavascript', 'Simple JavaScript'),
      description: t('workflow.simplejavascript.description', 'Execute custom JavaScript code with tag support and save results'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'processing'
    },
    {
      type: 'manualtag',
      icon: icon_genai,
      alt: 'Manual Tag',
      title: t('manualtag', 'Manual Tag'),
      description: t('workflow.manualtag.description', 'Create a new tag with static text or by combining existing tags'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'processing'
    },
    {
      type: 'triggerflow',
      icon: icon_callmcp,
      alt: 'Trigger Flow',
      title: t('workflow.triggerflow', 'Trigger Flow'),
      description: t('workflow.triggerflow.description', 'Trigger another flow execution from this flow'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'processing'
    },
    {
      type: 'looptriggerflow',
      icon: icon_callmcp,
      alt: 'Loop Trigger Flow',
      title: t('workflow.looptriggerflow', 'Loop Trigger Flow'),
      description: t(
        'workflow.looptriggerflow.description',
        'Split one tag into multiple loop items and run another flow once for each item.'
      ),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low'),
      costLevel: 'low',
      category: 'processing'
    },
    {
      type: 'dashboardsnapshot',
      icon: icon_javascript, // Temporary icon
      alt: 'Dashboard Snapshot',
      title: t('manualSnap.title', 'Dashboard Snapshot'),
      description: t('workflow.dashboardsnapshot.description', 'Automatically save a snapshot of all dashboard widgets'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'processing'
    },
    {
      type: 'validatereservation',
      icon: icon_validatelicense,
      alt: 'Validate Reservation',
      title: t('validatereservation', 'Validate Reservation'),
      description: t('workflow.validatereservation.description', 'Analyze documents for anomalies and potential fraud indicators'),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low'),
      costLevel: 'low',
      category: 'ai'
    },
    {
      type: 'validatedoctorlicense',
      icon: icon_cmqverifydoctor,
      alt: 'Validate Doctor License',
      title: t('validatedoctorlicense', 'Validate Doctor License (Rules)'),
      description: t('workflow.validatedoctorlicense.description', 'Process and standardize doctor license numbers using validation rules (no AI required)'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'processing'
    },
    {
      type: 'sendapi',
      icon: icon_sendapi,
      alt: 'Send API',
      title: t('workflow.send_api', 'Send API'),
      description: t('workflow.send_api.description', 'Send data to external services via API calls'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'integration'
    },
    {
      type: 'callmcp',
      icon: icon_callmcp,
      alt: 'Custom MCP',
      title: t('workflow.custom_mcp', 'Custom MCP'),
      description: t('workflow.custom_mcp.description', 'Use Model Context Protocol to integrate with custom AI models'),
      cost: t('workflow.ai_cost.medium', 'AI Cost: Medium/Depends on provider'),
      costLevel: 'medium',
      displayText: t('workflow.custom_mcp', 'Custom MCP (Experimental)'),
      category: 'ai',
      experimental: true
    },
    {
      type: 'msteamsSendMessage',
      icon: icon_sendmsgteams,
      alt: 'Send Teams Message',
      title: t('workflow.send_teams_message', 'Send Teams Message'),
      description: t('workflow.send_teams_message.description', 'Send automated messages to Microsoft Teams channels'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'integration'
    },
    {
      type: 'sendemail',
      icon: icon_sendemail,
      alt: 'Send email',
      title: t('workflow.send_email', 'Send Email'),
      description: t('workflow.send_email.description', 'Send automated emails with data and attachments'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      displayText: t('workflow.send_email', 'Send Email (Experimental)'),
      category: 'integration',
      experimental: true
    },
    {
      type: 'sendOutlookEmail',
      icon: icon_sendemail,
      alt: 'Send Outlook email',
      title: t('workflow.send_outlook_email.title', 'Send email (Outlook)'),
      description: t('workflow.send_outlook_email.description', 'Send authenticated Outlook emails through Microsoft Graph'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'integration'
    },
    {
      type: 'sendGmailEmail',
      icon: icon_sendemail,
      alt: 'Send Gmail email',
      title: t('workflow.send_gmail_email.title', 'Send email (Gmail)'),
      description: t('workflow.send_gmail_email.description', 'Send authenticated Gmail emails through the Gmail API'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'integration'
    },
    {
      type: 'sendLinkedinPost',
      icon: icon_sendmsgteams,
      alt: 'Publish LinkedIn post',
      title: t('workflow.send_linkedin_post.title', 'Publish post (LinkedIn)'),
      description: t('workflow.send_linkedin_post.description', 'Publish authenticated LinkedIn posts using dynamic tags'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'integration'
    },
    {
      type: 'sendLinkedinCompanyPost',
      icon: icon_sendmsgteams,
      alt: 'Publish LinkedIn company post',
      title: t('workflow.send_linkedin_company_post.title', 'Publish post (LinkedIn Company)'),
      description: t('workflow.send_linkedin_company_post.description', 'Publish authenticated LinkedIn company posts using dynamic tags'),
      cost: t('workflow.ai_cost.none', 'AI Cost: None'),
      costLevel: 'none',
      category: 'integration'
    },
    {
      type: 'weatherapi',
      icon: icon_sendapi,
      alt: 'Weather API',
      title: t('weatherapi', 'Weather API'),
      description: t('workflow.weatherapi.description', 'Fetch current weather, forecast, or history data'),
      cost: t('workflow.ai_cost.low', 'AI Cost: Low'),
      costLevel: 'low',
      category: 'integration'
    }
    ];

    const pluginMenuItems = (Array.isArray(pluginActions) ? pluginActions : [])
      .filter((plugin) => plugin?.loadFailed !== true)
      .map((plugin) => {
      const pluginType = String(plugin?.type || '').trim();
      const pluginName = String(plugin?.name || pluginType).trim();
      const isGeneratedPlugin = plugin?.isAIGenerated === true
        || /^auto_/i.test(pluginType)
        || /^plugin_auto_/i.test(pluginType)
        || /^auto_/i.test(pluginName)
        || /_auto_/i.test(pluginType)
        || /generated by ai/i.test(String(plugin?.description || ''));
      const category = ['processing', 'export', 'ai', 'integration'].includes(plugin?.category)
        ? plugin.category
        : 'processing';
      const costLevel = ['none', 'low', 'medium', 'high'].includes(plugin?.costLevel)
        ? plugin.costLevel
        : 'none';
    return {
      type: plugin.type,
      icon: plugin.icon || icon_default,
      alt: plugin.name || plugin.type,
        title: plugin.name || plugin.type,
        description: plugin.description || t('workflow.plugin.default_description', 'Configure this plugin action.'),
        cost: costLevel === 'none'
          ? t('workflow.ai_cost.none', 'AI Cost: None')
          : t(`workflow.ai_cost.${costLevel}`, `AI Cost: ${costLevel}`),
        costLevel,
        category,
        isAIGenerated: isGeneratedPlugin,
        pluginMemory: plugin?.memory && typeof plugin.memory === 'object' ? plugin.memory : null,
        plugin: true,
      };
    });

    return [...baseItems, ...pluginMenuItems];
  }, [t, pluginActions]);

  const selectedActionItem = useMemo(() => {
    return menuItems.find(i => i.type === selectedAction);
  }, [selectedAction, menuItems]);

  useEffect(() => {
    if (!createdActionTypeToOpen) {
      return;
    }
    const actionExists = menuItems.some((item) => item?.type === createdActionTypeToOpen);
    if (actionExists) {
      onMenuItemSelect(createdActionTypeToOpen, 'event');
      setCreatedActionTypeToOpen('');
      closeConnectorCreator();
      onClose();
    }
  }, [closeConnectorCreator, createdActionTypeToOpen, menuItems, onClose, onMenuItemSelect]);

  // Handle icon loading states
  const handleImageLoad = useCallback((itemType) => {
    setLoadingIcons(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemType);
      return newSet;
    });
  }, []);

  const handleImageError = useCallback((itemType) => {
    setLoadingIcons(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemType);
      return newSet;
    });
  }, []);

  const handleOpenPluginMemory = useCallback((event, item) => {
    event.preventDefault();
    event.stopPropagation();
    setPluginMemoryDialogState({
      open: true,
      pluginName: String(item?.title || item?.type || '').trim(),
      memory: item?.pluginMemory && typeof item.pluginMemory === 'object' ? item.pluginMemory : null
    });
  }, []);

  const handleClosePluginMemory = useCallback(() => {
    setPluginMemoryDialogState((prev) => ({
      ...prev,
      open: false
    }));
  }, []);

  // Initialize loading states when dialog opens
  useEffect(() => {
    if (open) {
      setLoadingIcons(new Set(menuItems.map(item => item.type)));
    }
  }, [open, menuItems]);

  // Get cost color based on cost level
  const getCostColor = (costLevel) => {
    switch (costLevel) {
      case 'none': return '#4caf50'; // Green
      case 'low': return '#ff9800'; // Orange
      case 'medium': return '#ff5722'; // Deep Orange
      case 'high': return '#f44336'; // Red
      default: return '#757575'; // Grey
    }
  };

  const categoryOrder = ['processing', 'export', 'ai', 'integration'];
  const categoryLabels = useMemo(() => ({
    processing: t('workflow.add_action_menu.categories.processing', 'Data Processing'),
    export: t('workflow.add_action_menu.categories.export'),
    ai: t('workflow.add_action_menu.categories.ai'),
    integration: t('workflow.add_action_menu.categories.integration')
  }), [t]);

  // Group items by category
  const groupedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Filter items based on search query
  const filteredGroupedItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return groupedItems;
    }

    const filtered = {};
    const query = searchQuery.toLowerCase();

    Object.keys(groupedItems).forEach(category => {
      const filteredItems = groupedItems[category].filter(item => {
        const titleMatch = (item.displayText || item.title).toLowerCase().includes(query);
        const descriptionMatch = item.description.toLowerCase().includes(query);
        const categoryMatch = categoryLabels[category]?.toLowerCase().includes(query);
        return titleMatch || descriptionMatch || categoryMatch;
      });

      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    });

    return filtered;
  }, [searchQuery, groupedItems, categoryLabels]);

  const handleActionClick = (actionType) => {
    setSelectedAction(actionType);
  };

  const handleConfirm = useCallback(() => {
    if (selectedAction) {
      onMenuItemSelect(selectedAction, selectedScope);
      setSelectedAction(null);
      setSelectedScope('event'); // Reset to default
      setSearchQuery(''); // Clear search when adding action
      onClose();
    }
  }, [selectedAction, selectedScope, onMenuItemSelect, onClose]);

  const handleCancel = useCallback(() => {
    setSelectedAction(null);
    setSelectedScope('event'); // Reset to default
    setSearchQuery(''); // Clear search when closing
    onClose();
  }, [onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!open) return;

      if (event.key === 'Escape') {
        handleCancel();
      } else if (event.key === 'Enter' && selectedAction) {
        handleConfirm();
      } else if (event.ctrlKey && event.key === 'k') {
        event.preventDefault();
        const searchInput = document.querySelector('[placeholder*="Search actions"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, selectedAction, handleCancel, handleConfirm]);

  // Clear search when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedAction(null);
      setSelectedScope('event'); // Reset to default when opening
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 3 },
          background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
          minHeight: { xs: '100%', md: '80vh' },
          maxHeight: { xs: '100%', md: '95vh' },
          m: { xs: 0, md: 2 },
          width: { xs: '100%', md: 'calc(100% - 32px)', lg: 'calc(100% - 64px)' }
        }
      }}
      TransitionComponent={Zoom}
      transitionDuration={300}
    >
      {/* Header */}
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        py: { xs: 1.5, md: 2.5 },
        px: { xs: 1, md: 3 }
      }}>
        <CloseButton
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: { xs: 4, md: 8 },
            top: { xs: 4, md: 8 },
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
          }}
        />
        <Typography variant="h4" component="div" sx={{
          fontWeight: 600,
          mb: { xs: 0.5, md: 1 },
          fontSize: { xs: '1.2rem', md: '1.6rem' },
          lineHeight: 1.2
        }}>
          ✨ {t('workflow.add_action_menu.title')}
        </Typography>
        <Typography variant="subtitle1" sx={{
          opacity: 0.9,
          fontSize: { xs: '0.75rem', md: '0.95rem' },
          display: { xs: 'none', sm: 'block' },
          mt: 0.5
        }}>
          {t('workflow.add_action_menu.subtitle')}
        </Typography>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: { xs: 1.5, md: 3 }, backgroundColor: '#fafbfc' }}>
        {!selectedAction ? (
          <>
            {/* Search Field */}
            <Box sx={{
              mb: { xs: 2, md: 3 },
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: '#fafbfc',
              pt: 1,
              pb: 2,
              borderBottom: '1px solid rgba(226, 232, 240, 0.6)',
              backdropFilter: 'blur(8px)'
            }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'stretch',
                  flexDirection: { xs: 'column', md: 'row' },
                  flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}
              >
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('workflow.add_action_menu.search_placeholder', '🔍 Search actions by name, description, or category... (Ctrl+K)')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{
                        color: searchQuery ? '#667eea' : '#94a3b8',
                        transition: 'color 0.2s ease-in-out'
                      }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <CloseButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        sx={{
                          color: '#94a3b8',
                          '&:hover': {
                            color: '#667eea',
                            backgroundColor: 'rgba(102, 126, 234, 0.1)'
                          }
                        }}
                      />
                    </InputAdornment>
                  )
                }}
                sx={{
                  flex: { xs: '1 1 auto', md: '1 1 0' },
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: searchQuery
                      ? '0 8px 32px rgba(102, 126, 234, 0.15)'
                      : '0 2px 12px rgba(0, 0, 0, 0.08)',
                    border: searchQuery ? '2px solid #667eea' : '1px solid #e2e8f0',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    fontSize: '1rem',
                    '& input': {
                      py: 1.5,
                      fontSize: '1rem'
                    },
                    '&:hover': {
                      boxShadow: '0 6px 24px rgba(102, 126, 234, 0.12)',
                      borderColor: '#667eea',
                      transform: 'translateY(-1px)'
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                      borderColor: '#667eea',
                      transform: 'translateY(-2px)'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
              />
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => openConnectorCreator({
                  defaultCreatorType: 'action',
                  lockCreatorType: true,
                  deferCloseAfterCreate: true,
                  onPluginCreated: (payload) => {
                    const actionType = String(payload?.created?.manifest?.type || '').trim();
                    if (actionType) {
                      setCreatedActionTypeToOpen(actionType);
                    }
                    if (caseid && typeof sendMessage === 'function') {
                      sendMessage({
                        command: 'getActionPlugins',
                        caseid,
                      });
                    }
                  },
                })}
                sx={{
                  minWidth: { xs: '100%', md: 250 },
                  flexShrink: 0,
                  px: 2.5,
                  py: 1.2,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontWeight: 700,
                  color: '#5757b0',
                  borderColor: 'rgba(87, 87, 176, 0.25)',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,249,255,1) 100%)',
                  boxShadow: '0 8px 24px rgba(87, 87, 176, 0.12)',
                  '&:hover': {
                    borderColor: '#5757b0',
                    background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(240,243,255,1) 100%)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
                  <Typography component="span" sx={{ fontWeight: 700 }}>
                    Plugins Creator
                  </Typography>
                  <Chip
                    size="small"
                    color="warning"
                    label={t('workflow.add_action_menu.labels.beta', 'BETA')}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.6rem',
                      height: 20,
                      pointerEvents: 'none',
                      borderRadius: '999px',
                    }}
                  />
                </Box>
              </Button>
              </Box>

              {/* Search Stats */}
              {searchQuery.trim() && (
                <Fade in={true} timeout={300}>
                  <Box sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}>
                    <Typography variant="body2" sx={{
                      color: '#64748b',
                      fontWeight: 500
                    }}>
                      {Object.values(filteredGroupedItems).reduce((acc, items) => acc + items.length, 0) === 0
                        ? t('workflow.add_action_menu.no_results_found', 'No results found')
                        : t('workflow.add_action_menu.results_found', `Found ${Object.values(filteredGroupedItems).reduce((acc, items) => acc + items.length, 0)} action${Object.values(filteredGroupedItems).reduce((acc, items) => acc + items.length, 0) !== 1 ? 's' : ''}`)
                      }
                    </Typography>
                    {Object.keys(filteredGroupedItems).map(category => (
                      <Chip
                        key={category}
                        label={`${categoryLabels[category]}: ${filteredGroupedItems[category].length}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Results */}
            {Object.keys(filteredGroupedItems).length === 0 && searchQuery.trim() ? (
              <Fade in={true} timeout={500}>
                <Box sx={{
                  textAlign: 'center',
                  py: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                  borderRadius: 4,
                  border: '2px dashed #e2e8f0',
                  mx: 2
                }}>
                  <Box sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.1,
                      position: 'absolute'
                    }} />
                    <SearchIcon sx={{
                      fontSize: 48,
                      color: '#667eea',
                      zIndex: 1
                    }} />
                  </Box>

                  <Box sx={{ maxWidth: 400 }}>
                    <Typography variant="h5" sx={{
                      color: '#334155',
                      fontWeight: 600,
                      mb: 1,
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}>
                      {t('workflow.add_action_menu.no_results', 'No actions found')}
                    </Typography>
                    <Typography variant="body1" sx={{
                      color: '#64748b',
                      lineHeight: 1.6,
                      mb: 3
                    }}>
                      {t('workflow.add_action_menu.no_results_description', `We couldn't find any actions matching "${searchQuery}". Try different keywords or browse all actions.`)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={() => setSearchQuery('')}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {t('workflow.add_action_menu.clear_search', 'Show all actions')}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => setSearchQuery('ai')}
                      sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1.5,
                        borderColor: '#667eea',
                        color: '#667eea',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#5a67d8',
                          backgroundColor: 'rgba(102, 126, 234, 0.05)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Search "AI"
                    </Button>
                  </Box>
                </Box>
              </Fade>
            ) : (
              categoryOrder.map((category) => {
                if (!filteredGroupedItems[category]) return null;

                return (
                  <Fade in={true} timeout={600} key={category}>
                    <Box sx={{ mb: 5 }}>
                      {/* Category Header */}
                      <Box sx={{
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap'
                      }}>
                        <Typography variant="h5" sx={{
                          fontWeight: 700,
                          color: '#1e293b',
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}>
                          {category === 'processing' && '⚙️'}
                          {category === 'export' && '📁'}
                          {category === 'ai' && '🤖'}
                          {category === 'integration' && '🔗'}
                          {categoryLabels[category]}
                        </Typography>

                        <Box sx={{
                          flex: 1,
                          height: '2px',
                          background: 'linear-gradient(90deg, rgba(102, 126, 234, 0.3) 0%, transparent 100%)',
                          borderRadius: 1
                        }} />

                        {searchQuery.trim() && (
                          <Zoom in={true} timeout={400}>
                            <Chip
                              label={`${filteredGroupedItems[category].length} result${filteredGroupedItems[category].length !== 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                backgroundColor: '#667eea',
                                color: 'white',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                '& .MuiChip-label': {
                                  px: 2
                                }
                              }}
                            />
                          </Zoom>
                        )}
                      </Box>

                      {/* Action Cards Grid */}
                      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ overflow: 'hidden' }}>
                        {filteredGroupedItems[category].map((item, index) => (
                          <Grid item xs={12} sm={6} lg={4} xl={3} key={item.type}>
                            <Fade in={true} timeout={500 + (index * 100)}>
                              <Card
                                sx={{
                                  height: '100%',
                                  cursor: 'pointer',
                                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                  border: selectedAction === item.type
                                    ? '3px solid #667eea'
                                    : '2px solid transparent',
                                  borderRadius: { xs: 3, md: 5 },
                                  background: selectedAction === item.type
                                    ? 'linear-gradient(145deg, rgba(102, 126, 234, 0.12), rgba(118, 75, 162, 0.08))'
                                    : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 249, 255, 0.95))',
                                  boxShadow: selectedAction === item.type
                                    ? '0 12px 48px rgba(102, 126, 234, 0.25)'
                                    : hoveredAction === item.type
                                      ? '0 16px 56px rgba(0, 0, 0, 0.12)'
                                      : '0 4px 24px rgba(0, 0, 0, 0.06)',
                                  transform: selectedAction === item.type
                                    ? 'scale(1.01) translateY(-4px)'
                                    : hoveredAction === item.type
                                      ? 'scale(1.01) translateY(-6px)'
                                      : 'translateY(0)',
                                  overflow: 'hidden',
                                  position: 'relative',
                                  maxWidth: '100%',
                                  '&:hover': {
                                    boxShadow: '0 20px 64px rgba(102, 126, 234, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)',
                                    '& .action-icon': {
                                      transform: 'scale(1.1)',
                                      filter: 'brightness(1.15) saturate(1.3)'
                                    },
                                    '& .action-title': {
                                      color: '#667eea'
                                    },
                                    '& .icon-container': {
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 12px 32px rgba(102, 126, 234, 0.3), 0 4px 16px rgba(102, 126, 234, 0.2)'
                                    }
                                  },
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: selectedAction === item.type
                                      ? 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                                      : 'transparent',
                                    transition: 'all 0.3s ease'
                                  }
                                }}
                                onMouseEnter={() => setHoveredAction(item.type)}
                                onMouseLeave={() => setHoveredAction(null)}
                              >
                                <CardActionArea
                                  onClick={() => handleActionClick(item.type)}
                                  sx={{ height: '100%', p: 0 }}
                                >
                                  <CardContent sx={{
                                    p: { xs: 2, md: 3 },
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden',
                                    minHeight: 0
                                  }}>
                                    {/* Icon and Title */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, md: 2 } }}>
                                      <Box sx={{
                                        width: { xs: 42, md: 64 },
                                        height: { xs: 42, md: 64 },
                                        minWidth: { xs: 42, md: 64 },
                                        mr: { xs: 1.5, md: 2 },
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                        transform: selectedAction === item.type ? 'scale(1.1)' : hoveredAction === item.type ? 'scale(1.05)' : 'scale(1)',
                                        background: item.type === 'generatewidget'
                                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                          : selectedAction === item.type
                                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            : 'linear-gradient(135deg, #f8f9ff 0%, #e8eaf6 100%)',
                                        borderRadius: '50%',
                                        boxShadow: selectedAction === item.type
                                          ? '0 12px 32px rgba(102, 126, 234, 0.4), 0 4px 16px rgba(102, 126, 234, 0.2)'
                                          : hoveredAction === item.type
                                            ? '0 8px 24px rgba(102, 126, 234, 0.25), 0 3px 12px rgba(102, 126, 234, 0.15)'
                                            : '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
                                        border: selectedAction === item.type || item.type === 'generatewidget'
                                          ? '3px solid rgba(255, 255, 255, 0.4)'
                                          : '2px solid rgba(102, 126, 234, 0.1)',
                                        '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          borderRadius: '50%',
                                          background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
                                          opacity: selectedAction === item.type ? 1 : 0.6,
                                          transition: 'opacity 0.3s ease'
                                        }
                                      }}
                                        className="icon-container">
                                        {/* Icon with loading state */}
                                        {loadingIcons.has(item.type) && (
                                          <Box sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: 'rgba(102, 126, 234, 0.1)',
                                            borderRadius: 'inherit',
                                            zIndex: 10
                                          }}>
                                            <Box sx={{
                                              width: { xs: 24, md: 36 },
                                              height: { xs: 24, md: 36 },
                                              border: '4px solid rgba(102, 126, 234, 0.2)',
                                              borderTop: '4px solid #667eea',
                                              borderRadius: '50%',
                                              animation: 'iconSpin 1.2s linear infinite',
                                              '@keyframes iconSpin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                              }
                                            }} />
                                          </Box>
                                        )}

                                        {item.type === 'generatewidget' ? (
                                          // Special treatment for generatewidget - use an icon instead of image
                                          <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: { xs: 30, md: 50 },
                                            height: { xs: 30, md: 50 },
                                            color: 'white',
                                            fontSize: { xs: '1.2rem', md: '2rem' },
                                            transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                            transform: selectedAction === item.type
                                              ? 'scale(1.15) rotate(5deg)'
                                              : hoveredAction === item.type
                                                ? 'scale(1.08) rotate(2deg)'
                                                : 'scale(1)',
                                            opacity: loadingIcons.has(item.type) ? 0 : 1,
                                            textShadow: '0 3px 12px rgba(0, 0, 0, 0.4)',
                                            filter: selectedAction === item.type
                                              ? 'drop-shadow(0 4px 12px rgba(255, 255, 255, 0.5))'
                                              : 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
                                            zIndex: 2
                                          }}>
                                            🧠✨
                                          </Box>
                                        ) : (
                                          <Box
                                            component="img"
                                            src={item.icon}
                                            alt={item.alt}
                                            className="action-icon"
                                            onLoad={() => handleImageLoad(item.type)}
                                            onError={() => handleImageError(item.type)}
                                            sx={{
                                              width: { xs: 24, md: 40 },
                                              height: { xs: 24, md: 40 },
                                              objectFit: 'contain',
                                              position: 'relative',
                                              zIndex: 2,
                                              transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                                              filter: selectedAction === item.type
                                                ? 'brightness(1.3) saturate(1.4) drop-shadow(0 4px 16px rgba(255, 255, 255, 0.6))'
                                                : hoveredAction === item.type
                                                  ? 'brightness(1.2) saturate(1.3) drop-shadow(0 3px 12px rgba(102, 126, 234, 0.4))'
                                                  : 'brightness(1.1) saturate(1.1) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.15))',
                                              transform: selectedAction === item.type
                                                ? 'scale(1.15)'
                                                : hoveredAction === item.type
                                                  ? 'scale(1.08)'
                                                  : 'scale(1)',
                                              opacity: loadingIcons.has(item.type) ? 0 : 1,
                                              maxWidth: '100%',
                                              maxHeight: '100%'
                                            }}
                                          />
                                        )}

                                        {/* Experimental badge overlay */}
                                        {item.experimental && (
                                          <Box sx={{
                                            position: 'absolute',
                                            top: -3,
                                            left: -3,
                                            width: { xs: 16, md: 20 },
                                            height: { xs: 16, md: 20 },
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                                            zIndex: 6,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 3px 12px rgba(255, 107, 53, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.4)',
                                            border: '2px solid white',
                                            animation: 'experimentalPulse 3s infinite',
                                            transform: selectedAction === item.type ? 'scale(1.1)' : 'scale(1)',
                                            transition: 'transform 0.3s ease',
                                            '@keyframes experimentalPulse': {
                                              '0%, 100%': {
                                                boxShadow: '0 3px 12px rgba(255, 107, 53, 0.5), inset 0 1px 2px rgba(255, 255, 255, 0.4)'
                                              },
                                              '50%': {
                                                boxShadow: '0 6px 20px rgba(255, 107, 53, 0.8), inset 0 1px 2px rgba(255, 255, 255, 0.6)'
                                              }
                                            },
                                            '&::before': {
                                              content: '""',
                                              position: 'absolute',
                                              top: 0,
                                              left: 0,
                                              right: 0,
                                              bottom: 0,
                                              borderRadius: '50%',
                                              background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 70%)'
                                            }
                                          }}>
                                            <Typography sx={{
                                              fontSize: { xs: '8px', md: '10px' },
                                              fontWeight: 900,
                                              color: 'white',
                                              lineHeight: 1,
                                              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                                              position: 'relative',
                                              zIndex: 1
                                            }}>
                                              β
                                            </Typography>
                                          </Box>
                                        )}
                                        {item.isAIGenerated && (
                                          <Tooltip title={t('generic_connector.memory_dialog_open', 'Show generation memory')} arrow>
                                            <Box
                                              role="button"
                                              tabIndex={0}
                                              onMouseDown={(event) => event.stopPropagation()}
                                              onClick={(event) => handleOpenPluginMemory(event, item)}
                                              onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                  handleOpenPluginMemory(event, item);
                                                }
                                              }}
                                              sx={{
                                                position: 'absolute',
                                                top: -4,
                                                right: -4,
                                                width: { xs: 18, md: 22 },
                                                height: { xs: 18, md: 22 },
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                                                zIndex: 7,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.45)',
                                                border: '2px solid white',
                                                cursor: 'pointer',
                                                p: 0,
                                                outline: 'none',
                                                pointerEvents: 'auto'
                                              }}
                                            >
                                              <AutoAwesomeIcon sx={{ fontSize: { xs: 10, md: 12 }, color: 'white' }} />
                                            </Box>
                                          </Tooltip>
                                        )}
                                      </Box>
                                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="h6"
                                          className="action-title"
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '0.85rem', md: '0.95rem' },
                                            color: selectedAction === item.type ? '#667eea' : '#1e293b',
                                            mb: 0.5,
                                            transition: 'color 0.3s ease',
                                            lineHeight: 1.2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            maxWidth: '100%',
                                            minHeight: { md: '2.4em' }
                                          }}
                                          title={item.displayText || item.title}
                                        >
                                          {item.displayText || item.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                          <Chip
                                            label={item.cost.replace('AI Cost: ', '')}
                                            size="small"
                                            sx={{
                                              fontSize: { xs: '0.65rem', md: '0.75rem' },
                                              fontWeight: 600,
                                              backgroundColor: getCostColor(item.costLevel),
                                              color: 'white',
                                              height: { xs: 20, md: 24 },
                                              boxShadow: `0 3px 12px ${getCostColor(item.costLevel)}40`,
                                              border: '1px solid rgba(255, 255, 255, 0.2)',
                                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                              '&:hover': {
                                                boxShadow: `0 4px 16px ${getCostColor(item.costLevel)}60`,
                                                transform: 'translateY(-1px) scale(1.02)'
                                              },
                                              '& .MuiChip-label': {
                                                px: 1,
                                                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                                              }
                                            }}
                                          />
                                          {item.experimental && (
                                            <Chip
                                              label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <Typography sx={{
                                                    fontSize: '10px',
                                                    fontWeight: 900,
                                                    lineHeight: 1
                                                  }}>
                                                    β
                                                  </Typography>
                                                  <Typography sx={{
                                                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                                                    fontWeight: 700,
                                                    lineHeight: 1
                                                  }}>
                                                    {t('workflow.add_action_menu.labels.beta')}
                                                  </Typography>
                                                </Box>
                                              }
                                              size="small"
                                              sx={{
                                                fontSize: { xs: '0.65rem', md: '0.75rem' },
                                                fontWeight: 700,
                                                background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                                                color: 'white',
                                                height: { xs: 20, md: 24 },
                                                boxShadow: '0 3px 12px rgba(255, 107, 53, 0.4)',
                                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                                animation: 'betaPulse 3s infinite',
                                                '@keyframes betaPulse': {
                                                  '0%, 100%': {
                                                    boxShadow: '0 3px 12px rgba(255, 107, 53, 0.4)',
                                                    transform: 'scale(1)'
                                                  },
                                                  '50%': {
                                                    boxShadow: '0 6px 20px rgba(255, 107, 53, 0.6)',
                                                    transform: 'scale(1.02)'
                                                  }
                                                },
                                                '& .MuiChip-label': {
                                                  px: 1,
                                                  py: 0
                                                }
                                              }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>

                                    {/* Description */}
                                    <Typography variant="body2" sx={{
                                      color: '#64748b',
                                      lineHeight: 1.4,
                                      fontSize: { xs: '0.8rem', md: '0.875rem' },
                                      flexGrow: 1,
                                      overflow: 'hidden',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {item.description}
                                    </Typography>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            </Fade>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Fade>
                );
              })
            )}
          </>
        ) : (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100%',
            py: 4,
            width: '100%',
            maxWidth: 800,
            mx: 'auto'
          }}>
            {/* Selected Action Header */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              mb: 5
            }}>
              <Box sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                mb: 3,
                p: 2,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f8f9ff 0%, #e8eaf6 100%)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid white'
              }}>
                {selectedActionItem?.type === 'generatewidget' ? (
                  <Typography sx={{ fontSize: '3rem' }}>🧠✨</Typography>
                ) : (
                  <Box
                    component="img"
                    src={selectedActionItem?.icon}
                    alt={selectedActionItem?.title}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                )}
              </Box>

              <Typography variant="h4" sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: { xs: '1.4rem', md: '1.7rem' },
                color: '#1e293b'
              }}>
                {selectedActionItem?.displayText || selectedActionItem?.title}
              </Typography>

              <Typography variant="body1" sx={{
                color: '#64748b',
                maxWidth: 600,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                lineHeight: 1.6
              }}>
                {selectedActionItem?.description}
              </Typography>
            </Box>

            {/* Scope Selection */}
            <Box sx={{ width: '100%', mb: 4 }}>
              <Typography variant="h6" sx={{
                textAlign: 'center',
                mb: 3,
                fontWeight: 600,
                color: '#334155'
              }}>
                {t('settings.scope_label', 'Run:')}
              </Typography>

              <Grid container spacing={3} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Card
                    onClick={() => setSelectedScope('event')}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      border: selectedScope === 'event' ? '3px solid #667eea' : '2px solid transparent',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'visible',
                      boxShadow: selectedScope === 'event'
                        ? '0 12px 32px rgba(102, 126, 234, 0.2)'
                        : '0 4px 12px rgba(0,0,0,0.05)',
                      transform: selectedScope === 'event' ? 'translateY(-4px)' : 'none',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3, height: '100%', textAlign: 'center' }}>
                      <Box sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        backgroundColor: selectedScope === 'event' ? 'rgba(102, 126, 234, 0.1)' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <DescriptionIcon sx={{
                          fontSize: 32,
                          color: selectedScope === 'event' ? '#667eea' : '#64748b'
                        }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('settings.scope_each_event', 'On Event')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('settings.scope_event_desc', 'Run for each new or updated individual event')}
                      </Typography>
                      {selectedScope === 'event' && (
                        <Box sx={{
                          position: 'absolute',
                          top: -12,
                          right: -12,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: '#667eea',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)'
                        }}>
                          ✓
                        </Box>
                      )}
                    </CardActionArea>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card
                    onClick={() => setSelectedScope('project')}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      border: selectedScope === 'project' ? '3px solid #c35a5f' : '2px solid transparent',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      overflow: 'visible',
                      boxShadow: selectedScope === 'project'
                        ? '0 12px 32px rgba(195, 90, 95, 0.2)'
                        : '0 4px 12px rgba(0,0,0,0.05)',
                      transform: selectedScope === 'project' ? 'translateY(-4px)' : 'none',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3, height: '100%', textAlign: 'center' }}>
                      <Box sx={{
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 2,
                        borderRadius: '50%',
                        backgroundColor: selectedScope === 'project' ? 'rgba(195, 90, 95, 0.1)' : '#f1f5f9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FolderIcon sx={{
                          fontSize: 32,
                          color: selectedScope === 'project' ? '#c35a5f' : '#64748b'
                        }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {t('settings.scope_once_project', 'On Events / On Widget')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('settings.scope_project_desc', 'Run once when all events/files are processed or widget is changing')}
                      </Typography>
                      {selectedScope === 'project' && (
                        <Box sx={{
                          position: 'absolute',
                          top: -12,
                          right: -12,
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: '#c35a5f',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(195, 90, 95, 0.4)'
                        }}>
                          ✓
                        </Box>
                      )}
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>      {/* Actions */}
      <DialogActions sx={{
        p: { xs: 1.5, md: 3 },
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        gap: { xs: 1.5, md: 2.5 },
        position: 'sticky',
        bottom: 0,
        backdropFilter: 'blur(8px)',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        alignItems: 'center',
        justifyContent: { xs: 'stretch', sm: 'space-between' },
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {selectedAction ? (
          <Button
            onClick={() => setSelectedAction(null)}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: 3,
              px: { xs: 2, sm: 3 },
              py: { xs: 1, md: 1.5 },
              borderColor: '#cbd5e1',
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.9rem', md: '1rem' },
              minWidth: { xs: '100px', sm: '120px' },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f1f5f9',
                transform: 'translateY(-1px)'
              }
            }}
          >
            {t('common.back', 'Back')}
          </Button>
        ) : (
          <Button
            onClick={handleCancel}
            variant="outlined"
            size="large"
            sx={{
              borderRadius: 3,
              px: { xs: 2, sm: 3 },
              py: { xs: 1, md: 1.5 },
              borderColor: '#cbd5e1',
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: { xs: '0.9rem', md: '1rem' },
              minWidth: { xs: '100px', sm: '120px' },
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: '#94a3b8',
                backgroundColor: '#f1f5f9',
                transform: 'translateY(-1px)'
              }
            }}
          >
            {t('workflow.add_action_menu.buttons.cancel')}
          </Button>
        )}

        {selectedAction && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            size="large"
            sx={{
              borderRadius: 3,
              px: { xs: 3, sm: 4 },
              py: { xs: 1, md: 1.5 },
              fontWeight: 700,
              textTransform: 'none',
              fontSize: { xs: '0.9rem', md: '1rem' },
              minWidth: { xs: '140px', sm: '160px' },
              width: { xs: '100%', sm: 'auto' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 6px 24px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.5)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            {t('workflow.add_action_menu.buttons.add_action', 'Add Action')}
          </Button>
        )}
      </DialogActions>
      <PluginMemoryDialog
        open={pluginMemoryDialogState.open}
        onClose={handleClosePluginMemory}
        memory={pluginMemoryDialogState.memory}
        pluginName={pluginMemoryDialogState.pluginName}
      />
    </Dialog>
  );
};

export default AddActionMenu;
